import { PaymentStatus } from '@/api/ariadne/__generated_types__/apollo';
import * as dataSelectors from '@/data/data-selectors';
import { translator } from 'deckard-ui/dist/utils/translator';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ActionsModule } from './components/actions-module/actions-module';
import { AdditionalFilters } from './components/additional-filters/additional-filters';
import { TopLevelTOTAGGridTable } from './components/ag-grid-table/top-level-tot-ag-grid-table';
import { PeriodSelector } from './components/filters/period-selector';
import { SearchInputField } from './components/filters/search-input';
import { TaxStatusFilter } from './components/filters/tax-status-filter';
import { Summary } from './components/summary/summary';
import {
  PaymentPeriodDateRange,
  ReportingPeriod,
  TopLevelTOTFilters,
} from './types';
import {
  mostRecentCompletedPaymentPeriod,
  paymentPeriodDateRangeFromISODate,
} from './utils/date-utils';

export function TOTView() {
  const stats = useSelector(dataSelectors.rentalscapeStats);
  const reportingPeriodType: ReportingPeriod = stats.taxReportingSchedule;

  const translations = useSelector(dataSelectors.translations);

  const [period, setPeriod] = useState<PaymentPeriodDateRange>(
    paymentPeriodDateRangeFromISODate(
      reportingPeriodType,
      mostRecentCompletedPaymentPeriod(reportingPeriodType)
    )
  );

  const [searchQuery, setSearchQuery] = useState<string>('');

  const [taxStatus, setTaxStatus] = useState<PaymentStatus | undefined>();
  const [filters, setFilters] = useState<TopLevelTOTFilters>({
    reportingStatus: [],
    penalties: [],
    interest: [],
    auditing: [],
    removeZeroTaxableReceipts: false,
  });

  const translate = useMemo(
    () => translator(translations ?? {}),
    [translations]
  );

  return (
    <div className="p-5 overflow-y-scroll" data-testid="top-level-tot-view">
      <div className="flex flex-col gap-2">
        <ActionsModule
          period={period}
          searchQuery={searchQuery}
          taxStatus={taxStatus}
          filters={filters}
        />

        <div className="flex flex-col justify-between w-full gap-2 xl:flex-row">
          <div className="flex flex-col gap-2 max-w-[500px] min-w-[500px]">
            <h2 className="text-lg">Selected Tax Period</h2>

            <PeriodSelector
              reportingPeriodType={reportingPeriodType}
              period={period}
              setPeriod={setPeriod}
            />

            <SearchInputField
              searchQuery={searchQuery}
              onChange={setSearchQuery}
              translate={translate}
            />

            <div className="flex gap-2">
              <TaxStatusFilter taxStatus={taxStatus} onChange={setTaxStatus} />

              <AdditionalFilters onFiltersChange={setFilters} />
            </div>
          </div>

          <Summary period={period} />
        </div>

        <TopLevelTOTAGGridTable
          filters={filters}
          period={period}
          searchQuery={searchQuery}
          taxStatus={taxStatus}
          translate={translate}
        />
      </div>
    </div>
  );
}
